/* eslint-disable */
// REST client for the Payora payroll module. Replaces the original Next.js
// server actions + server-component reads with FastAPI endpoints under
// `${REACT_APP_BACKEND_URL}/api/payora`.

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/payora`;

export type EmployeeInput = {
  fullName?: string;
  firstName: string; lastName: string; email: string; phone: string;
  position: string; department: string; employeeType: "313" | "261";
  monthlySalary?: string;
  dailyRate: string; hourlyRate?: string; minutesRate?: string; hiredAt: string; taxExempt: boolean;
  tinNumber: string;
  sssNumber: string;
  philhealthNumber: string;
  pagibigNumber: string;
  allowanceMf: string;
  allowanceGas: string;
  allowanceLoad: string;
  allowanceHousing: string;
  govHdmf: boolean;
  govHdmfNumber: string;
  govSss: boolean;
  govPhilhealth: boolean;
  govTax: boolean;
};

export type LoanInput = {
  employeeId: string;
  loanType: string;
  principal: string;
  perPeriodDeduction: string;
  deductTo: "Payroll" | "Mobility Fund";
  startDate: string;
  disbursementDate: string;
  repaymentTerms: string;
  interestRate: string;
  remarks: string;
};

export type LoanUpdateInput = LoanInput & {
  expectedUpdatedAt: string;
};

export type SssBracketRow = { salaryFrom: string; salaryTo: string; employeeShare: string };
export type TaxBracketRow = { salaryFrom: string; salaryTo: string; baseTax: string; rateOver: string };
export type PresetRowInput = {
  label: string;
  mode: "monthly" | "fixed";
  startDay: string;
  endDay: string;
  monthOffset: string;
  startDate: string;
  endDate: string;
  startMonthOffset?: string;
};

export const LOAN_TYPES = [
  "SSS Salary Loan", "Pag-IBIG Loan", "Cash Advance", "Company Loan", "Emergency Loan",
] as const;

export const LOAN_DEDUCT_TO_OPTIONS = ["Payroll", "Mobility Fund"] as const;

function qs(params: Record<string, string | undefined>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

async function getJSON(path: string) {
  const r = await fetch(`${BASE}${path}`, { headers: getAuthHeaders() });
  return r.json();
}

async function send(path: string, method: string, body?: any) {
  try {
    const r = await fetch(`${BASE}${path}`, {
      method,
      headers: getAuthHeaders(method),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return await r.json();
  } catch (e: any) {
    return { ok: false, error: e?.message || "Network error." };
  }
}

function getAuthHeaders(method = "GET") {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  try {
    const token = localStorage.getItem("token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const csrf = localStorage.getItem("csrf_token");
    if (csrf && ["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase())) {
      headers["X-CSRF-Token"] = csrf;
    }
  } catch (_) {
    // Ignore storage access errors so tests and SSR-like environments still work.
  }
  return headers;
}

/* ---- reads ---- */
export const getDashboard = () => getJSON("/dashboard");
export const listEmployees = (f: Record<string, string>) => getJSON(`/employees${qs(f)}`);
export const listLoans = (f: Record<string, string>) => getJSON(`/loans${qs(f)}`);
export const listPayroll = (ledger: string, f: Record<string, string>) =>
  getJSON(`/payroll${qs({ ledger, ...f })}`);
export const getPayrollDetail = (id: number, ledger: string, f: Record<string, string>) =>
  getJSON(`/payroll/${id}${qs({ ledger, ...f })}`);
export const getPreferences = () => getJSON("/preferences");
export async function getDepartments(): Promise<string[]> {
  return getJSON("/departments");
}

/* ---- employee actions ---- */
export const createEmployee = (input: EmployeeInput) => send("/employees", "POST", input);
export const updateEmployee = (id: number, input: EmployeeInput) => send(`/employees/${id}`, "PUT", input);
export const toggleEmployeeStatus = (id: number) => send(`/employees/${id}/toggle`, "POST");

/* ---- loan actions ---- */
export const createLoan = (input: LoanInput) => send("/loans", "POST", input);
export const updateLoan = (id: number, input: LoanUpdateInput) => send(`/loans/${id}`, "PUT", input);

/* ---- payroll actions ---- */
export const generatePayrollAction = (input: {
  name: string; startDate: string; endDate: string; holidays: any[]; ledger?: string;
}) => send(`/payroll/generate${qs({ ledger: input.ledger || "payroll" })}`, "POST", input);
export const lockPayrollAction = (input: {
  name: string; startDate: string; endDate: string; holidays: any[]; ledger?: string;
}) => send(`/payroll/lock${qs({ ledger: input.ledger || "payroll" })}`, "POST", input);
export const savePayrollAttendanceOverridesAction = (input: {
  name: string; startDate: string; endDate: string; holidays: any[]; ledger?: string; attendanceOverrides?: Record<string, Record<string, string>>;
}) => send(`/payroll/attendance-overrides${qs({ ledger: input.ledger || "payroll" })}`, "POST", input);
export const savePayrollSalaryAdjustmentsAction = (input: {
  name: string; startDate: string; endDate: string; holidays: any[]; ledger?: string; attendanceOverrides?: Record<string, Record<string, string>>;
  salaryAdjustments?: Record<string, Array<{ title: string; value: string; unit: string }>>;
}) => send(`/payroll/salary-adjustments${qs({ ledger: input.ledger || "payroll" })}`, "POST", input);
export const savePayrollLoanSkipsAction = (input: {
  name: string; startDate: string; endDate: string; holidays: any[]; ledger?: string; attendanceOverrides?: Record<string, Record<string, string>>;
  salaryAdjustments?: Record<string, Array<{ title: string; value: string; unit: string }>>;
  loanSkips?: Record<string, number[]>;
}) => send(`/payroll/loan-skips${qs({ ledger: input.ledger || "payroll" })}`, "POST", input);
export const voidPayrollAction = (periodId: number, pin: string, ledger: string = "payroll") =>
  send(`/payroll/${periodId}/void`, "POST", { pin, ledger });

/* ---- preference actions ---- */
export const saveSssBrackets = (rows: SssBracketRow[]) => send("/sss/save", "POST", rows);
export const applySssBrackets = (rows: SssBracketRow[]) => send("/sss/apply", "POST", rows);
export const saveTaxBrackets = (rows: TaxBracketRow[]) => send("/tax/save", "POST", rows);
export const applyTaxBrackets = (rows: TaxBracketRow[]) => send("/tax/apply", "POST", rows);
export const savePayrollPresets = (rows: PresetRowInput[]) => send("/presets/save", "POST", rows);
export const applyPayrollPresets = (rows: PresetRowInput[]) => send("/presets/apply", "POST", rows);
